import { useEffect, useState } from 'react'
import { Callout, Card, CardContent, CardHeader, KeyValueList, StatusPill } from './ui'
import {
  contractConfigurationApi,
  type ContractConfigurationRefs,
  type ContractConfigurationSnapshot,
  type SnapshotEntityRef,
} from '../api/configurationTemplates'

/**
 * F-081 (TASK-0335, REQ-221-001, REQ-296-001): pannello "Configurazione usata" della pratica. Mostra i
 * riferimenti versionati registrati alla creazione e all'invio al backoffice: versione di prodotto,
 * operatore e settore del catalogo, campi dinamici (codice e versione), impronta del set di regole di
 * completezza e versione del piano provvigionale. Sono RIFERIMENTI: non c'è alcun importo e non cambiano
 * se la configurazione viene modificata dopo. Le pratiche precedenti a questa funzione non hanno
 * snapshot e non vengono ricostruite.
 */
const ROME = 'Europe/Rome'

function formatRome(iso: string): string {
  return new Date(iso).toLocaleString('it-IT', {
    timeZone: ROME,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const TRIGGER_LABELS: Record<string, string> = {
  Creazione: 'Alla creazione',
  Invio: 'All\'invio al backoffice',
}

function entityLabel(ref: SnapshotEntityRef | null): string {
  if (!ref) return '—'
  return ref.version === null ? `#${ref.id} (nessuna versione registrata)` : `#${ref.id} · versione ${ref.version}`
}

function Refs({ refs }: { refs: ContractConfigurationRefs }) {
  return (
    <KeyValueList items={[
      { label: 'Prodotto', value: entityLabel(refs.product) },
      { label: 'Operatore', value: entityLabel(refs.operator) },
      { label: 'Settore', value: entityLabel(refs.department) },
      {
        label: 'Campi dinamici',
        value: refs.dynamicFields.length === 0
          ? '—'
          : refs.dynamicFields.map(f => (
              <StatusPill key={f.code} tone="neutral" className="me-1">{f.code} v{f.version}</StatusPill>
            )),
      },
      {
        label: 'Regole di completezza',
        value: <>{refs.validationRules.count} attive · impronta <code>{refs.validationRules.fingerprint}</code></>,
      },
      {
        label: 'Piano provvigionale',
        value: refs.commissionPlanVersion === null ? '—' : `versione ${refs.commissionPlanVersion}`,
      },
    ]} />
  )
}

export function ContractConfigurationSnapshots({ contractId }: { contractId: number }) {
  const [snapshots, setSnapshots] = useState<ContractConfigurationSnapshot[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!Number.isFinite(contractId)) return
    let cancelled = false
    setLoading(true)
    setError(null)
    contractConfigurationApi.snapshots(contractId)
      .then(rows => { if (!cancelled) setSnapshots(rows) })
      .catch(e => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Errore nel caricamento della configurazione usata')
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [contractId])

  return (
    <Card variant="outlined" className="mb-4">
      <CardHeader icon="tune" title="Configurazione usata" />
      <CardContent>
        {loading && <p className="text-muted mb-0">Caricamento…</p>}
        {error && <Callout tone="error">{error}</Callout>}
        {!loading && !error && snapshots.length === 0 && (
          <p className="text-muted mb-0">
            Nessuna configurazione registrata per questa pratica: è precedente a questa funzione e non viene ricostruita.
          </p>
        )}
        {snapshots.map((s, i) => (
          <div key={s.id} className={i > 0 ? 'mt-3 pt-3 border-top' : ''}>
            <div className="md-typescale-emphasized-body-medium mb-1">
              {TRIGGER_LABELS[s.trigger] ?? s.trigger}
              <span className="text-muted ms-2 md-typescale-body-small">{formatRome(s.capturedAt)}</span>
            </div>
            {s.refs ? <Refs refs={s.refs} /> : <p className="text-muted md-typescale-body-small mb-0">Riferimenti non leggibili.</p>}
          </div>
        ))}
      </CardContent>
    </Card>
  )
}
